import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import { ShoppingBagIcon, XMarkIcon } from '@heroicons/react/24/outline';
import Checkout from '../components/Checkout';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { 
  getCart, 
  saveCart, 
  addToCart, 
  removeFromCart, 
  updateCartQuantity, 
  calculateCartTotal 
} from '../utils/cartUtils';

// Product Type Card Component
const ProductTypeCard = ({ productType, gender, products, onSelect }) => {
  const hasStock = products.some(p => p.total_quantity > 0);
  const totalStock = products.reduce((sum, p) => sum + p.total_quantity, 0);
  const price = products[0]?.price || 0;
  const onSale = products.some(p => p.on_sale);
  const salePrice = products.find(p => p.on_sale)?.sale_price || price * 0.8;

  return (
    <div className="group bg-white rounded-xl overflow-hidden border border-gray-200 hover:border-primary-500 transition-all duration-300 hover:transform hover:-translate-y-1 shadow-lg hover:shadow-xl">
      {/* Image Section */}
      <div className="relative overflow-hidden">
        {products[0]?.image_url ? (
          <img 
            src={products[0].image_url} 
            alt={productType}
            className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-64 bg-gray-700 flex items-center justify-center">
            <ShoppingBagIcon className="w-16 h-16 text-gray-500" />
          </div>
        )}
        
        {/* Sale Badge */}
        {onSale && (
          <div className="absolute top-4 left-4">
            <span className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold">
              SALE
            </span>
          </div>
        )}

        {/* Stock Indicator */}
        <div className="absolute bottom-4 left-4">
          <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
            totalStock > 20 ? 'bg-green-600 text-white' : 
            totalStock > 0 ? 'bg-yellow-600 text-white' : 'bg-red-600 text-white'
          }`}>
            {totalStock > 0 ? `${totalStock} left` : 'Out of stock'}
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="p-6">
        <div className="mb-4">
          <h3 className="text-xl font-bold text-gray-900 mb-2">{productType}</h3>
          <p className="text-gray-600 text-sm">
            {gender === 'M' ? 'Men\'s' : 'Women\'s'} Collection
          </p>
        </div>
        
        {/* Price Section */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-baseline space-x-2">
            {onSale ? (
              <>
                <span className="text-2xl font-bold text-primary-600">
                  ${salePrice.toFixed(2)}
                </span>
                <span className="text-lg text-gray-500 line-through">
                  ${price}
                </span>
              </>
            ) : (
              <span className="text-2xl font-bold text-gray-900">
                ${price}
              </span>
            )}
          </div>
        </div>

        {/* Action Button */}
        <button
          onClick={() => onSelect(productType, gender, products)}
          disabled={!hasStock}
          className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-300 ${
            hasStock 
              ? 'bg-primary-600 hover:bg-primary-700 text-white shadow-lg hover:shadow-xl' 
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          {!hasStock ? 'Out of Stock' : 'Select Options'}
        </button>
      </div>
    </div>
  );
};

// Cart Item Component
const CartItem = ({ item, onUpdateQuantity, onRemove }) => (
  <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg border border-gray-200 hover:border-gray-300 transition-all duration-300">
    <div className="flex items-center space-x-4">
      {item.image && (
        <div className="relative">
          <img src={item.image} alt={item.name} className="w-16 h-16 object-cover rounded-lg" />
          <div className="absolute -top-2 -right-2 bg-primary-600 text-white text-xs font-bold px-2 py-1 rounded-full">
            {item.quantity}
          </div>
        </div>
      )}
      <div>
        <h4 className="text-gray-900 font-semibold">{item.name}</h4>
        <p className="text-gray-600 text-sm">Size: {item.size} • {item.gender === 'M' ? 'Men\'s' : 'Women\'s'}</p>
        <p className="text-primary-600 font-semibold">${item.price}</p>
      </div>
    </div>
    <div className="flex items-center space-x-3">
      <div className="flex items-center space-x-2 bg-gray-200 rounded-lg p-1">
        <button
          onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
          className="bg-gray-300 hover:bg-gray-400 text-gray-700 w-6 h-6 rounded flex items-center justify-center transition-colors"
        >
          -
        </button>
        <span className="text-gray-900 font-semibold min-w-[1.5rem] text-center">{item.quantity}</span>
        <button
          onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
          className="bg-gray-300 hover:bg-gray-400 text-gray-700 w-6 h-6 rounded flex items-center justify-center transition-colors"
        >
          +
        </button>
      </div>
      <button
        onClick={() => onRemove(item.id)}
        className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm font-medium transition-colors"
      >
        Remove
      </button>
    </div>
  </div>
);

// Product Selection Modal Component
const ProductSelectionModal = ({ 
  selectedProductType, 
  selectedGender, 
  availableProducts, 
  selectedSize, 
  quantity,
  onClose, 
  onSizeSelect, 
  onQuantityChange, 
  onAddToCart 
}) => {
  // Group products by size and combine stock
  const groupedProducts = useMemo(() => {
    const grouped = {};
    availableProducts.forEach(product => {
      if (!grouped[product.size]) {
        grouped[product.size] = {
          size: product.size,
          total_quantity: 0,
          price: product.price,
          on_sale: product.on_sale,
          sale_price: product.sale_price,
          product_id: product.id // Keep the first product ID for reference
        };
      }
      grouped[product.size].total_quantity += product.total_quantity;
    });
    return Object.values(grouped);
  }, [availableProducts]);

  const selectedProduct = useMemo(() => 
    groupedProducts.find(p => p.size === selectedSize), 
    [groupedProducts, selectedSize]
  );

  const price = useMemo(() => {
    if (!selectedProduct) return 0;
    return selectedProduct.on_sale 
      ? (selectedProduct.sale_price || selectedProduct.price * 0.8) 
      : selectedProduct.price;
  }, [selectedProduct]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg max-w-2xl w-full p-6 border border-gray-700">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold text-white">
            {selectedGender === 'M' ? 'Male' : 'Female'} {selectedProductType}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-full"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>
        
        <div className="mb-6">
          <h4 className="text-white font-semibold mb-4">Available Sizes & Stock:</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {groupedProducts.map((product) => (
              <button
                key={product.size}
                onClick={() => onSizeSelect(product.size)}
                disabled={product.total_quantity === 0}
                className={`p-3 rounded border transition-all text-center ${
                  selectedSize === product.size
                    ? 'bg-primary-500 border-primary-500 text-white'
                    : product.total_quantity === 0
                    ? 'bg-gray-700 border-gray-600 text-gray-500 cursor-not-allowed'
                    : 'bg-gray-700 border-gray-600 text-gray-300 hover:border-primary-500'
                }`}
              >
                <div className="font-semibold">{product.size}</div>
                <div className={`text-xs mt-1 ${
                  product.total_quantity > 10 ? 'text-green-400' : 
                  product.total_quantity > 0 ? 'text-yellow-400' : 'text-red-400'
                }`}>
                  {product.total_quantity > 0 ? `${product.total_quantity} left` : 'Out of stock'}
                </div>
              </button>
            ))}
          </div>
        </div>

        {selectedSize && (
          <div className="mb-6">
            <label className="block text-white font-semibold mb-2">Quantity</label>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => onQuantityChange(Math.max(1, quantity - 1))}
                className="bg-gray-600 hover:bg-gray-500 text-white w-10 h-10 rounded-full flex items-center justify-center"
              >
                -
              </button>
              <span className="text-white font-semibold text-lg">{quantity}</span>
              <button
                onClick={() => {
                  if (quantity < selectedProduct.total_quantity) {
                    onQuantityChange(quantity + 1);
                  }
                }}
                className="bg-gray-600 hover:bg-gray-500 text-white w-10 h-10 rounded-full flex items-center justify-center"
              >
                +
              </button>
            </div>
          </div>
        )}

        <button
          onClick={() => {
            // Find the original product from availableProducts for cart addition
            const originalProduct = availableProducts.find(p => p.size === selectedSize);
            if (originalProduct) {
              onAddToCart(originalProduct, selectedSize, quantity);
            }
          }}
          disabled={!selectedSize}
          className="w-full bg-primary-500 hover:bg-primary-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-lg transition-all duration-300"
        >
          {!selectedSize ? 'Select a Size' : `Add to Cart - $${price.toFixed(2)}`}
        </button>
      </div>
    </div>
  );
};

// Main Merchandise Component
const Merchandise = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();

  // State Management
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [selectedProductType, setSelectedProductType] = useState(null);
  const [selectedGender, setSelectedGender] = useState(null);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [selectedSize, setSelectedSize] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [showCheckout, setShowCheckout] = useState(false);

  // Memoized organized products
  const organizedProducts = useMemo(() => {
    const organized = {
      M: { 'T-Shirt': [], 'Singlet': [] },
      F: { 'T-Shirt': [], 'Singlet': [] }
    };
    
    products.forEach(product => {
      if (organized[product.gender] && organized[product.gender][product.name]) {
        organized[product.gender][product.name].push(product);
      }
    });
    
    return organized;
  }, [products]);

  // Memoized cart total
  const cartTotal = useMemo(() => calculateCartTotal(cart), [cart]);

  // Data fetching
  const fetchProducts = useCallback(async () => {
    try {
      const productsRef = collection(db, 'products');
      const q = query(productsRef, orderBy('created_at', 'desc'));
      const snapshot = await getDocs(q);
      const productsData = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(p => p.active === true);
      setProducts(productsData);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCart = useCallback(() => {
    const savedCart = getCart();
    setCart(savedCart);
  }, []);

  // Cart management functions
  const saveCartToStorage = useCallback((newCart) => {
    saveCart(newCart);
    setCart(newCart);
  }, []);

  const addToCartHandler = useCallback((product, size, qty) => {
    const cartItem = {
      id: `${product.id}-${size}`,
      productId: product.id,
      name: product.name,
      price: product.on_sale ? product.sale_price || product.price * 0.8 : product.price,
      size,
      gender: product.gender,
      quantity: qty,
      image: product.image_url
    };

    const newCart = addToCart(cart, cartItem);
    saveCartToStorage(newCart);

    // Reset selection state
    setSelectedProductType(null);
    setSelectedGender(null);
    setAvailableProducts([]);
    setSelectedSize('');
    setQuantity(1);
  }, [cart, saveCartToStorage]);

  const removeFromCartHandler = useCallback((itemId) => {
    const newCart = removeFromCart(cart, itemId);
    saveCartToStorage(newCart);
  }, [cart, saveCartToStorage]);

  const updateCartQuantityHandler = useCallback((itemId, newQuantity) => {
    const newCart = updateCartQuantity(cart, itemId, newQuantity);
    saveCartToStorage(newCart);
  }, [cart, saveCartToStorage]);

  // Product selection handlers
  const handleProductTypeSelect = useCallback((productType, gender, products) => {
    setSelectedProductType(productType);
    setSelectedGender(gender);
    setAvailableProducts(products);
  }, []);

  const handleSizeSelect = useCallback((size) => {
    setSelectedSize(size);
  }, []);

  const handleQuantityChange = useCallback((newQuantity) => {
    setQuantity(newQuantity);
  }, []);

  const handleAddToCart = useCallback((product, size, qty) => {
    if (product) {
      addToCartHandler(product, size, qty);
    }
  }, [addToCartHandler]);

  const handleCloseModal = useCallback(() => {
    setSelectedProductType(null);
    setSelectedGender(null);
    setAvailableProducts([]);
    setSelectedSize('');
    setQuantity(1);
  }, []);

  const isProfileCompleteForPurchase = useCallback(() => {
    if (!userProfile) return false;
    const first = (userProfile.first_name || '').trim();
    const last = (userProfile.last_name || '').trim();
    const contact = (userProfile.contact_number || '').trim();
    const cp = userProfile.competition_profile || {};
    const coach = (userProfile.coach_name || cp.coach_name || '').trim();
    return !!(first && last && contact && coach);
  }, [userProfile]);

  const handleProceedToCheckout = useCallback(() => {
    if (!userProfile) {
      toast.error('Please log in and complete your profile before checkout.');
      navigate('/profile');
      return;
    }
    if (!isProfileCompleteForPurchase()) {
      toast.error('Please complete your profile (name, contact, and coach) before purchasing. You will be redirected to your profile.');
      navigate('/profile');
      return;
    }
    setShowCheckout(true);
  }, [userProfile, isProfileCompleteForPurchase, navigate]);

  // Effects
  useEffect(() => {
    fetchProducts();
    loadCart();
  }, [fetchProducts, loadCart]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-br from-gray-900 via-secondary-900 to-primary-900">
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Merchandise
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Discover our exclusive collection of athletic wear designed for champions
            </p>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Product Categories */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {/* Male T-Shirt */}
            {organizedProducts.M['T-Shirt'].length > 0 && (
              <ProductTypeCard 
                productType="T-Shirt" 
                gender="M" 
                products={organizedProducts.M['T-Shirt']} 
                onSelect={handleProductTypeSelect} 
              />
            )}
            
            {/* Male Singlet */}
            {organizedProducts.M['Singlet'].length > 0 && (
              <ProductTypeCard 
                productType="Singlet" 
                gender="M" 
                products={organizedProducts.M['Singlet']} 
                onSelect={handleProductTypeSelect} 
              />
            )}
            
            {/* Female T-Shirt */}
            {organizedProducts.F['T-Shirt'].length > 0 && (
              <ProductTypeCard 
                productType="T-Shirt" 
                gender="F" 
                products={organizedProducts.F['T-Shirt']} 
                onSelect={handleProductTypeSelect} 
              />
            )}
            
            {/* Female Singlet */}
            {organizedProducts.F['Singlet'].length > 0 && (
              <ProductTypeCard 
                productType="Singlet" 
                gender="F" 
                products={organizedProducts.F['Singlet']} 
                onSelect={handleProductTypeSelect} 
              />
            )}
          </div>

          {/* Cart Section */}
          <div id="cart-section" className="bg-white rounded-lg p-6 border border-gray-200 shadow-lg">
            <div className="flex items-center space-x-3 mb-3">
              <ShoppingBagIcon className="w-6 h-6 text-primary-600" />
              <h2 className="text-2xl font-bold text-gray-900">Shopping Cart</h2>
              {cart.length > 0 && (
                <span className="bg-primary-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                  {cart.length} {cart.length === 1 ? 'item' : 'items'}
                </span>
              )}
            </div>
            {cart.length === 0 ? (
              <div className="text-center py-8">
                <ShoppingBagIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-lg">Your cart is empty</p>
                <p className="text-gray-500 text-sm mt-2">Add some items to get started</p>
              </div>
            ) : (
              <>
                <div className="space-y-4 mb-6">
                  {cart.map((item) => (
                    <CartItem
                      key={item.id}
                      item={item}
                      onUpdateQuantity={updateCartQuantityHandler}
                      onRemove={removeFromCartHandler}
                    />
                  ))}
                </div>
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-xl font-bold text-gray-900">Total</span>
                    <span className="text-2xl font-bold text-primary-600">
                      ${cartTotal.toFixed(2)}
                    </span>
                  </div>
                  <button 
                    onClick={handleProceedToCheckout}
                    className="w-full font-semibold py-3 px-6 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl bg-primary-600 text-white hover:bg-primary-700"
                  >
                    Proceed to Checkout
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Product Selection Modal */}
      {selectedProductType && (
        <ProductSelectionModal
          selectedProductType={selectedProductType}
          selectedGender={selectedGender}
          availableProducts={availableProducts}
          selectedSize={selectedSize}
          quantity={quantity}
          onClose={handleCloseModal}
          onSizeSelect={handleSizeSelect}
          onQuantityChange={handleQuantityChange}
          onAddToCart={handleAddToCart}
        />
      )}

      {/* Checkout Modal */}
      {showCheckout && (
        <Checkout
          cart={cart}
          total={cartTotal}
          onClose={() => setShowCheckout(false)}
          onOrderComplete={() => {
            // Cart will be cleared by HitPay redirect
            // Success will be shown after payment completion via redirect URL
            setShowCheckout(false);
          }}
        />
      )}
    </div>
  );
};

export default Merchandise;